import { useMutation } from '@tanstack/react-query';

import ExternalService from '../services/ExternalService/ExternalApiService';
import { type Rule } from '../services/types';

interface ApiResponse {
  success: boolean; // Indicates if the API call was successful
  errorMessage?: string; // Optional message from the server
  data?: Rule; // The actual data returned by the API
  error?: string | null; // Error details in case of failure
  status?: number;
  ok?: boolean;
}

/**
 * Sends a request to create a new rule via the external API service.
 *
 * @async
 * @function postRule
 * @param {Rule} rule - The rule object to be created.
 * @returns {Promise<Rule>} The created rule object returned from the API.
 */
const postRule = async (rule: Rule) => {
  const response = (await ExternalService.createRule(rule)) as ApiResponse;

  return response;
};

/**
 * Custom hook to handle the creation of rules using React Query's `useMutation`.
 *
 * @function useCreateRule
 * @returns {import('@tanstack/react-query').UseMutationResult<Rule, unknown, Rule, unknown>} A mutation result object containing mutation methods and state.
 */

const useCreateRule = () =>
  useMutation({
    mutationFn: (rule: Rule) => postRule(rule),
    onSuccess: (data) => data,
  });

export default useCreateRule;
